import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Search,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Clock,
  Receipt,
  Loader2,
  RefreshCw,
  X,
  Building2,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Eye,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MoneyInput } from '../components/ui/money-input';
import { supplierInvoiceService } from '../services/supplierInvoiceService';
import cashBoxService, { CashBox } from '../services/cashBoxService';
import { localDocumentStore } from '../services/localDocumentStore';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../constants/permissions';
import RequirePermission from '../components/RequirePermission';
import {
  SupplierInvoiceResponse,
  SupplierInvoicePaymentStatus,
  TransactionResponseDTO,
} from '../types';
import { formatMoney } from '../utils/format';
import { cashBoxMatchesAccountingType } from '../utils/accountingType';
import { showSuccess, showError } from '../components/Alerts';

// ==================== HELPERS ====================

const ITEMS_PER_PAGE = 12;

const statusConfig: Record<SupplierInvoicePaymentStatus, { label: string; color: string; icon: React.ReactNode }> = {
  PENDIENTE: {
    label: 'Pendiente',
    color: 'bg-destructive/10 text-destructive border-destructive/20',
    icon: <Clock className="w-3 h-3" />,
  },
  PARCIALMENTE_PAGADA: {
    label: 'Parcial',
    color: 'bg-warning/10 text-warning border-warning/30',
    icon: <AlertCircle className="w-3 h-3" />,
  },
  PAGADA: {
    label: 'Pagada',
    color: 'bg-success/10 text-success border-success/20',
    icon: <CheckCircle2 className="w-3 h-3" />,
  },
};

const formatCurrency = (amount: number, currency: string) => {
  const symbol = currency === 'USD' ? 'US$' : '$';
  return `${symbol} ${formatMoney(amount)}`;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-AR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const formatDateTime = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// ==================== COMPONENTE PRINCIPAL ====================

export default function SupplierInvoices() {
  const { hasPermission } = useAuth();
  const canManageInvoices = hasPermission(PERMISSIONS.COMPRAS.FACTURAS_EDITAR);

  const [invoices, setInvoices] = useState<SupplierInvoiceResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | SupplierInvoicePaymentStatus>('all');
  const [currencyFilter, setCurrencyFilter] = useState<'all' | 'ARS' | 'USD'>('all');
  const [page, setPage] = useState(1);

  const [selectedInvoice, setSelectedInvoice] = useState<SupplierInvoiceResponse | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [transactions, setTransactions] = useState<TransactionResponseDTO[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  const [isPayOpen, setIsPayOpen] = useState(false);
  const [invoiceToPay, setInvoiceToPay] = useState<SupplierInvoiceResponse | null>(null);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
  const [payForm, setPayForm] = useState({ cashBoxId: 0, amount: 0, description: '' });
  const [payErrors, setPayErrors] = useState<string[]>([]);
  const [isPaying, setIsPaying] = useState(false);

  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerMime, setViewerMime] = useState('application/pdf');

  const openFileViewer = async (fileUrl: string, title: string) => {
    let blobUrl: string | null = null;
    if (fileUrl.startsWith('local://')) {
      const docId = fileUrl.replace('local://', '');
      blobUrl = await localDocumentStore.createObjectUrl(docId);
      if (!blobUrl) {
        showError('El archivo no está disponible localmente.');
        return;
      }
      setViewerMime('application/pdf');
    } else if (fileUrl.startsWith('http')) {
      blobUrl = fileUrl;
      setViewerMime('application/pdf');
    } else {
      showError('URL de archivo no reconocida');
      return;
    }
    setViewerTitle(title);
    setViewerUrl(blobUrl);
  };

  const closeFileViewer = () => {
    if (viewerUrl && viewerUrl.startsWith('blob:')) URL.revokeObjectURL(viewerUrl);
    setViewerUrl(null);
    setViewerTitle('');
    setViewerMime('application/pdf');
  };

  // ==================== CARGA ====================

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const data = await supplierInvoiceService.getAllInvoices();
      setInvoices(data);
    } catch (err: any) {
      showError(err?.message || 'Error al cargar las facturas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  // ==================== FILTRADO ====================

  const filteredInvoices = invoices.filter((inv) => {
    const matchesSearch =
      inv.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.supplierName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inv.purchaseOrderNumber || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || inv.paymentStatus === statusFilter;
    const matchesCurrency = currencyFilter === 'all' || inv.currency === currencyFilter;
    return matchesSearch && matchesStatus && matchesCurrency;
  });

  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || currencyFilter !== 'all';
  const clearFilters = () => { setSearchTerm(''); setStatusFilter('all'); setCurrencyFilter('all'); setPage(1); };

  const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE);
  const safePage = Math.min(page, Math.max(1, totalPages));
  const pageInvoices = filteredInvoices.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  useEffect(() => { setPage(1); }, [searchTerm, statusFilter, currencyFilter]);

  // ==================== RESUMEN ====================

  const summary = {
    total: filteredInvoices.length,
    pendientes: filteredInvoices.filter((i) => i.paymentStatus === 'PENDIENTE').length,
    parciales: filteredInvoices.filter((i) => i.paymentStatus === 'PARCIALMENTE_PAGADA').length,
    pagadas: filteredInvoices.filter((i) => i.paymentStatus === 'PAGADA').length,
    totalPendienteARS: filteredInvoices
      .filter((i) => i.currency === 'ARS' && i.paymentStatus !== 'PAGADA')
      .reduce((sum, i) => sum + i.pendingAmount, 0),
    totalPendienteUSD: filteredInvoices
      .filter((i) => i.currency === 'USD' && i.paymentStatus !== 'PAGADA')
      .reduce((sum, i) => sum + i.pendingAmount, 0),
  };

  // ==================== DETALLE ====================

  const handleViewDetail = async (invoice: SupplierInvoiceResponse) => {
    setSelectedInvoice(invoice);
    setIsDetailOpen(true);
    setLoadingTransactions(true);
    try {
      const txs = await supplierInvoiceService.getInvoiceTransactions(invoice.id);
      setTransactions(txs);
    } catch (err: any) {
      setTransactions([]);
      showError(err?.response?.data?.message || err?.message || 'No se pudo cargar el historial de pagos');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const closeDetail = () => { setIsDetailOpen(false); setSelectedInvoice(null); };

  // ==================== PAGO ====================

  const handleOpenPay = async (invoice: SupplierInvoiceResponse) => {
    setInvoiceToPay(invoice);
    setPayForm({ cashBoxId: 0, amount: invoice.pendingAmount, description: '' });
    setPayErrors([]);
    setIsPayOpen(true);
    try {
      const boxes = await cashBoxService.getAllCashBoxes();
      const filtered = boxes.filter(
        (b) =>
          b.isActive &&
          b.currency === invoice.currency &&
          cashBoxMatchesAccountingType(b.accountingType, invoice.accountingType),
      );
      setCashBoxes(filtered);
      if (filtered.length > 0) setPayForm((prev) => ({ ...prev, cashBoxId: filtered[0].id }));
    } catch {
      setCashBoxes([]);
    }
  };

  const handlePay = async () => {
    if (!invoiceToPay) return;

    const errors: string[] = [];
    if (!payForm.cashBoxId) errors.push('Debe seleccionar una caja');
    if (payForm.amount <= 0) errors.push('El monto debe ser mayor a 0');
    if (payForm.amount > invoiceToPay.pendingAmount) {
      errors.push(`El monto no puede exceder el pendiente (${formatCurrency(invoiceToPay.pendingAmount, invoiceToPay.currency)})`);
    }
    const selectedBox = cashBoxes.find((b) => b.id === payForm.cashBoxId);
    if (selectedBox && selectedBox.balance < payForm.amount) {
      errors.push(`Saldo insuficiente en ${selectedBox.name} (saldo: ${formatCurrency(selectedBox.balance, selectedBox.currency)})`);
    }
    if (errors.length > 0) { setPayErrors(errors); return; }

    setIsPaying(true);
    setPayErrors([]);
    try {
      const updated = await supplierInvoiceService.payInvoice(invoiceToPay.id, {
        cashBoxId: payForm.cashBoxId,
        amount: payForm.amount,
        description: payForm.description || undefined,
      });
      setInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? updated : inv)));
      showSuccess(`Pago de ${formatCurrency(payForm.amount, invoiceToPay.currency)} registrado exitosamente`);
      setIsPayOpen(false);
      setInvoiceToPay(null);
    } catch (err: any) {
      setPayErrors([err?.response?.data?.message || err?.message || 'Error al registrar el pago']);
    } finally {
      setIsPaying(false);
    }
  };

  // ==================== RENDER ====================

  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

        {/* ── Header ── */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Facturas de Proveedores</h1>
                <p className="mt-1 text-sm text-muted-foreground">Consulta y gestión de facturas recibidas de proveedores</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                <Receipt className="w-3.5 h-3.5" />
                {invoices.length} factura{invoices.length !== 1 ? 's' : ''}
              </span>
              <Button variant="outline" size="sm" onClick={loadInvoices} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
            </div>
          </div>
        </div>

        {/* ── Cuerpo ── */}
        <div className="p-4 space-y-6 sm:p-6">

          {/* Stats + filtros de estado */}
          <div className="bg-background border border-border rounded-xl overflow-hidden">
            <div className="flex flex-col gap-4 px-4 pt-4 pb-3 sm:flex-row sm:items-center sm:justify-between">

              {/* Chips de estado */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-0.5 shrink-0">
                  Estado:
                </span>
                {([
                  {
                    filter: 'all' as const,
                    label: 'Todas', value: summary.total,
                    icon: Receipt,
                    activeBg: 'bg-foreground', activeText: 'text-background',
                    idleBg: 'bg-muted/60', idleIcon: 'text-muted-foreground',
                  },
                  {
                    filter: 'PENDIENTE' as const,
                    label: 'Pendientes', value: summary.pendientes,
                    icon: Clock,
                    activeBg: 'bg-destructive', activeText: 'text-destructive-foreground',
                    idleBg: 'bg-destructive/8 hover:bg-destructive/15', idleIcon: 'text-destructive',
                  },
                  {
                    filter: 'PARCIALMENTE_PAGADA' as const,
                    label: 'Parciales', value: summary.parciales,
                    icon: AlertCircle,
                    activeBg: 'bg-warning', activeText: 'text-warning-foreground',
                    idleBg: 'bg-warning/8 hover:bg-warning/15', idleIcon: 'text-warning',
                  },
                  {
                    filter: 'PAGADA' as const,
                    label: 'Pagadas', value: summary.pagadas,
                    icon: CheckCircle2,
                    activeBg: 'bg-success', activeText: 'text-success-foreground',
                    idleBg: 'bg-success/8 hover:bg-success/15', idleIcon: 'text-success',
                  },
                ] as const).map((s) => {
                  const active = statusFilter === s.filter;
                  return (
                    <button
                      key={s.filter}
                      onClick={() => setStatusFilter(s.filter)}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        active
                          ? `${s.activeBg} ${s.activeText} border-transparent shadow-sm`
                          : `${s.idleBg} text-foreground border-border`
                      }`}
                    >
                      <s.icon className={`w-3 h-3 ${active ? '' : s.idleIcon}`} />
                      {s.label}
                      <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums min-w-[18px] text-center ${
                        active ? 'bg-black/10 dark:bg-white/15' : 'bg-border text-muted-foreground'
                      }`}>
                        {s.value}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Deuda pendiente */}
              {(summary.totalPendienteARS > 0 || summary.totalPendienteUSD > 0) && (
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                    Deuda:
                  </span>
                  {summary.totalPendienteARS > 0 && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-destructive/25 bg-destructive/6 text-destructive">
                      <DollarSign className="w-3 h-3 shrink-0" />
                      <span className="text-xs font-bold tabular-nums">$ {formatMoney(summary.totalPendienteARS)}</span>
                      <span className="text-[10px] font-medium opacity-70">ARS</span>
                    </div>
                  )}
                  {summary.totalPendienteUSD > 0 && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-info/25 bg-info/6 text-info">
                      <DollarSign className="w-3 h-3 shrink-0" />
                      <span className="text-xs font-bold tabular-nums">US$ {formatMoney(summary.totalPendienteUSD)}</span>
                      <span className="text-[10px] font-medium opacity-70">USD</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Barra de progreso */}
            {summary.total > 0 && (
              <div className="px-4 pb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-medium text-muted-foreground">
                    {summary.pagadas} de {summary.total} factura{summary.total !== 1 ? 's' : ''} pagada{summary.pagadas !== 1 ? 's' : ''}
                  </span>
                  <span className="text-[10px] font-bold text-muted-foreground tabular-nums">
                    {summary.total > 0 ? Math.round((summary.pagadas / summary.total) * 100) : 0}%
                  </span>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${summary.total > 0 ? (summary.pagadas / summary.total) * 100 : 0}%`,
                      background: 'linear-gradient(90deg, hsl(var(--success)) 0%, hsl(var(--success)/0.7) 100%)',
                    }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Filtros de búsqueda */}
          <div className="flex flex-col items-end gap-4 md:flex-row">
            <div className="flex flex-wrap gap-4">
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Moneda</label>
                <Select value={currencyFilter} onValueChange={(v) => setCurrencyFilter(v as any)}>
                  <SelectTrigger className="w-[160px] rounded-lg bg-background border-border">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las monedas</SelectItem>
                    <SelectItem value="ARS">ARS — Pesos</SelectItem>
                    <SelectItem value="USD">USD — Dólares</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <div className="space-y-2">
                  <label className="text-xs font-medium uppercase text-transparent select-none">·</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 h-10 text-muted-foreground hover:text-foreground rounded-lg"
                  >
                    <X className="w-3.5 h-3.5" />
                    Limpiar
                  </Button>
                </div>
              )}
            </div>

            <div className="w-full space-y-2 md:w-72 md:ml-auto">
              <label className="text-xs font-medium uppercase text-muted-foreground">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nº factura, proveedor, OC..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="rounded-lg pl-9 bg-background border-border focus-visible:ring-primary"
                />
              </div>
            </div>
          </div>

          {/* Lista de facturas */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">Cargando facturas...</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <FileText className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">No se encontraron facturas</h3>
              <p className="mb-6 text-sm text-muted-foreground max-w-xs mx-auto">
                {hasActiveFilters ? 'Intentá ajustar los filtros.' : 'Las facturas se cargan desde las órdenes de compra.'}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters}>Limpiar filtros</Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {pageInvoices.map((inv) => {
                  const st = statusConfig[inv.paymentStatus];
                  const isUSD = inv.currency === 'USD';
                  const isPaid = inv.paymentStatus === 'PAGADA';

                  return (
                    <div
                      key={inv.id}
                      className="flex flex-col bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow duration-200"
                    >
                      {/* Card header */}
                      <div className="flex items-start justify-between px-4 pt-4 pb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex-shrink-0 p-2.5 rounded-xl bg-primary/10">
                            <FileText className="w-4 h-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground truncate leading-tight">
                              {inv.invoiceNumber}
                            </p>
                            <span className={`inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
                              isUSD ? 'border-info/30 bg-info/10 text-info' : 'border-border bg-muted text-muted-foreground'
                            }`}>
                              {inv.currency}
                            </span>
                          </div>
                        </div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium border rounded-full shrink-0 ${st.color}`}>
                          {st.icon}
                          {st.label}
                        </span>
                      </div>

                      <div className="mx-4 border-t border-border" />

                      {/* Info */}
                      <div className="px-4 py-3 space-y-2 flex-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs font-medium text-foreground truncate">{inv.supplierName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                          <span className="text-xs text-muted-foreground">{formatDate(inv.invoiceDate)}</span>
                          {inv.purchaseOrderNumber && (
                            <span className="text-xs text-muted-foreground truncate ml-auto">OC {inv.purchaseOrderNumber}</span>
                          )}
                        </div>

                        {/* Montos */}
                        <div className="pt-1 space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-[11px] text-muted-foreground">Total</span>
                            <span className="text-xs font-semibold text-foreground tabular-nums">
                              {formatCurrency(inv.totalAmount, inv.currency)}
                            </span>
                          </div>
                          {inv.amountPaid > 0 && (
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-muted-foreground">Pagado</span>
                              <span className="text-xs text-muted-foreground tabular-nums">
                                {formatCurrency(inv.amountPaid, inv.currency)}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center justify-between pt-1 border-t border-border/60">
                            <span className="text-[11px] font-medium text-muted-foreground">Pendiente</span>
                            <span className={`text-sm font-bold tabular-nums ${isPaid ? 'text-success' : 'text-destructive'}`}>
                              {formatCurrency(inv.pendingAmount, inv.currency)}
                            </span>
                          </div>
                        </div>

                        {/* Archivo adjunto */}
                        {inv.fileUrl && (inv.fileUrl.startsWith('local://') || inv.fileUrl.startsWith('http')) && (
                          <div className="pt-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); openFileViewer(inv.fileUrl!, inv.invoiceNumber); }}
                              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                            >
                              <Eye className="w-3 h-3" />
                              Ver archivo adjunto
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Footer */}
                      <div className="mt-auto mx-4 border-t border-border" />
                      <div className="flex items-center justify-between px-4 py-2.5 gap-2">
                        <button
                          onClick={() => handleViewDetail(inv)}
                          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-primary transition-colors"
                        >
                          Ver detalle
                          <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        {!isPaid && canManageInvoices && (
                          <Button
                            size="sm"
                            onClick={() => handleOpenPay(inv)}
                            className="h-7 px-2.5 text-xs rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                          >
                            <DollarSign className="w-3 h-3 mr-1" />
                            Pagar
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginación */}
              <div className="flex items-center justify-between pt-4 mt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Mostrando <span className="font-semibold text-foreground">{pageInvoices.length}</span> de{' '}
                  <span className="font-semibold text-foreground">{filteredInvoices.length}</span> factura{filteredInvoices.length !== 1 ? 's' : ''}
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={safePage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-foreground">{safePage} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={safePage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* ==================== MODAL DETALLE ==================== */}
        <Dialog open={isDetailOpen} onOpenChange={(open) => { if (!open) closeDetail(); }}>
          <DialogContent className="w-[95vw] max-w-xl bg-card border-border p-0 max-h-[90vh] flex flex-col">
            {selectedInvoice && (
              <>
                <DialogHeader className="shrink-0 px-5 pt-5 pb-4 border-b border-border">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-primary" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-medium text-muted-foreground">Factura</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium border rounded-full ${statusConfig[selectedInvoice.paymentStatus].color}`}>
                            {statusConfig[selectedInvoice.paymentStatus].icon}
                            {statusConfig[selectedInvoice.paymentStatus].label}
                          </span>
                        </div>
                        <DialogTitle className="text-lg font-bold text-foreground leading-tight">
                          {selectedInvoice.invoiceNumber}
                        </DialogTitle>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">{selectedInvoice.supplierName}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0 pr-6">
                      <p className="text-[11px] text-muted-foreground uppercase tracking-wider">Total</p>
                      <p className="text-xl font-bold text-foreground tabular-nums leading-tight">
                        {formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border/60 flex-wrap">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Fecha</p>
                      <p className="text-xs font-medium text-foreground mt-0.5">{formatDate(selectedInvoice.invoiceDate)}</p>
                    </div>
                    <div className="w-px h-6 bg-border" />
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Moneda</p>
                      <p className="text-xs font-medium text-foreground mt-0.5">{selectedInvoice.currency}</p>
                    </div>
                    {selectedInvoice.purchaseOrderNumber && (
                      <>
                        <div className="w-px h-6 bg-border" />
                        <div>
                          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Orden de Compra</p>
                          <p className="text-xs font-medium text-foreground mt-0.5">{selectedInvoice.purchaseOrderNumber}</p>
                        </div>
                      </>
                    )}
                  </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto divide-y divide-border/60">
                  {/* Montos */}
                  <div className="px-5 py-4">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Montos</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Subtotal</span>
                        <span className="text-sm font-medium text-foreground tabular-nums">{formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">IVA</span>
                        <span className="text-sm font-medium text-foreground tabular-nums">{formatCurrency(selectedInvoice.ivaTotal, selectedInvoice.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border/60">
                        <span className="text-sm font-semibold text-foreground">Total</span>
                        <span className="text-sm font-bold text-foreground tabular-nums">{formatCurrency(selectedInvoice.totalAmount, selectedInvoice.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Pagado</span>
                        <span className="text-sm font-medium text-muted-foreground tabular-nums">{formatCurrency(selectedInvoice.amountPaid, selectedInvoice.currency)}</span>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-border/60">
                        <span className="text-sm font-semibold text-foreground">Pendiente</span>
                        <span className={`text-base font-bold tabular-nums ${selectedInvoice.pendingAmount > 0 ? 'text-destructive' : 'text-success'}`}>
                          {formatCurrency(selectedInvoice.pendingAmount, selectedInvoice.currency)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Archivo adjunto */}
                  {selectedInvoice.fileUrl && (selectedInvoice.fileUrl.startsWith('local://') || selectedInvoice.fileUrl.startsWith('http')) && (
                    <div className="px-5 py-4">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Archivo Adjunto</p>
                      <div className="flex items-center gap-3 px-3 py-2.5 border rounded-xl border-border bg-muted/30">
                        <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                          <FileText className="w-4 h-4 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{selectedInvoice.invoiceNumber}</p>
                          <p className="text-xs text-muted-foreground">Factura adjunta</p>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => openFileViewer(selectedInvoice.fileUrl!, selectedInvoice.invoiceNumber)}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Ver
                          </button>
                          <button
                            onClick={() => window.open(selectedInvoice.fileUrl!.startsWith('local://') ? '#' : selectedInvoice.fileUrl!, '_blank', 'noopener,noreferrer')}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-muted text-muted-foreground hover:bg-muted/80 border border-border transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Notas */}
                  {selectedInvoice.notes && (
                    <div className="px-5 py-4">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notas</p>
                      <p className="text-sm text-foreground leading-relaxed">{selectedInvoice.notes}</p>
                    </div>
                  )}

                  {/* Historial de pagos */}
                  <div className="px-5 py-4">
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Historial de Pagos</p>
                    {loadingTransactions ? (
                      <div className="flex items-center justify-center py-6 gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Cargando...</span>
                      </div>
                    ) : transactions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-6 text-center">
                        <CreditCard className="w-7 h-7 mb-2 text-muted-foreground/40" />
                        <p className="text-sm text-muted-foreground">Sin pagos registrados</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        {transactions.map((tx) => {
                          const debitEntry = tx.entries.find(
                            (e) => e.entryType === 'DEBITO' && e.accountType === 'CAJA',
                          );
                          return (
                            <div key={tx.id} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-muted/40">
                              <div className="min-w-0">
                                <p className="text-xs font-medium text-foreground truncate">{tx.description || 'Pago registrado'}</p>
                                <p className="text-[11px] text-muted-foreground mt-0.5">{formatDateTime(tx.executedAt)}</p>
                              </div>
                              <p className="text-sm font-bold text-amount tabular-nums shrink-0">
                                {debitEntry ? formatCurrency(debitEntry.amount, debitEntry.currency) : '—'}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer modal detalle */}
                <div className="shrink-0 flex items-center justify-between px-5 py-3 border-t border-border">
                  <Button variant="outline" size="sm" onClick={closeDetail} className="rounded-lg">
                    Cerrar
                  </Button>
                  {selectedInvoice.paymentStatus !== 'PAGADA' && canManageInvoices && (
                    <Button
                      size="sm"
                      onClick={() => { closeDetail(); handleOpenPay(selectedInvoice); }}
                      className="rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                    >
                      <DollarSign className="w-4 h-4 mr-1.5" />
                      Registrar Pago
                    </Button>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* ==================== VISOR DE ARCHIVO ==================== */}
        {viewerUrl && (
          <Dialog open={!!viewerUrl} onOpenChange={(open) => { if (!open) closeFileViewer(); }}>
            <DialogContent className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0 gap-0">
              <DialogHeader className="flex-shrink-0 px-4 py-3 border-b border-border">
                <div className="flex items-center justify-between gap-4">
                  <DialogTitle className="flex items-center gap-2 text-sm font-semibold truncate">
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    {viewerTitle}
                  </DialogTitle>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => window.open(viewerUrl!, '_blank', 'noopener,noreferrer')}
                    >
                      <ExternalLink className="w-3 h-3 mr-1.5" />
                      Abrir en pestaña
                    </Button>
                    <button
                      onClick={closeFileViewer}
                      className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </DialogHeader>
              <div className="flex-1 overflow-hidden bg-muted/30">
                {viewerMime.startsWith('image/') ? (
                  <img src={viewerUrl} alt={viewerTitle} className="w-full h-full object-contain" />
                ) : (
                  <iframe src={viewerUrl} title={viewerTitle} className="w-full h-full border-0" allow="fullscreen" />
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* ==================== MODAL PAGO ==================== */}
        <Dialog open={isPayOpen} onOpenChange={(open) => { if (!open) { setIsPayOpen(false); setInvoiceToPay(null); } }}>
          <DialogContent className="w-[95vw] max-w-lg bg-card border-border p-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
              <DialogTitle className="flex items-center gap-2 text-foreground">
                <DollarSign className="w-5 h-5 text-primary" />
                Registrar Pago
              </DialogTitle>
            </DialogHeader>

            {invoiceToPay && (
              <div className="px-6 pb-6 pt-5 space-y-5">
                <div className="rounded-xl bg-muted/40 px-4 py-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Factura</span>
                    <span className="text-sm font-bold text-foreground">{invoiceToPay.invoiceNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Proveedor</span>
                    <span className="text-sm font-medium text-foreground">{invoiceToPay.supplierName}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Total factura</span>
                    <span className="text-sm font-medium text-foreground">{formatCurrency(invoiceToPay.totalAmount, invoiceToPay.currency)}</span>
                  </div>
                  {invoiceToPay.amountPaid > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">Ya pagado</span>
                      <span className="text-sm text-muted-foreground">{formatCurrency(invoiceToPay.amountPaid, invoiceToPay.currency)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-border/60">
                    <span className="text-sm font-medium text-muted-foreground">Pendiente a pagar</span>
                    <span className="text-base font-bold text-destructive">{formatCurrency(invoiceToPay.pendingAmount, invoiceToPay.currency)}</span>
                  </div>
                </div>

                {payErrors.length > 0 && (
                  <div className="p-3 space-y-1 text-sm text-destructive border border-destructive/20 bg-destructive/5 rounded-lg">
                    {payErrors.map((err, i) => <p key={i}>• {err}</p>)}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                      Caja ({invoiceToPay.currency}) <span className="text-xs text-red-500">(Campo obligatorio)</span>
                    </Label>
                    {cashBoxes.length === 0 ? (
                      <div className="p-3 text-sm border rounded-lg text-warning border-warning/30 bg-warning/5">
                        No hay cajas disponibles en {invoiceToPay.currency}
                        {invoiceToPay.accountingType ? ` para contabilidad ${invoiceToPay.accountingType}` : ''}.
                      </div>
                    ) : (
                      <Select
                        value={String(payForm.cashBoxId)}
                        onValueChange={(v) => setPayForm((prev) => ({ ...prev, cashBoxId: Number(v) }))}
                      >
                        <SelectTrigger className="rounded-lg bg-background border-border">
                          <SelectValue placeholder="Seleccionar caja" />
                        </SelectTrigger>
                        <SelectContent>
                          {cashBoxes.map((box) => (
                            <SelectItem key={box.id} value={String(box.id)}>
                              {box.name} — Saldo: {formatCurrency(box.balance, box.currency)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-foreground sm:text-sm">
                        Monto a pagar <span className="text-xs text-red-500">(Campo obligatorio)</span>
                      </Label>
                      <button
                        type="button"
                        onClick={() => setPayForm((prev) => ({ ...prev, amount: invoiceToPay.pendingAmount }))}
                        className="text-xs font-medium text-primary hover:opacity-80"
                      >
                        Pagar total pendiente
                      </button>
                    </div>
                    <MoneyInput
                      value={payForm.amount === 0 ? '' : payForm.amount}
                      onValueChange={(value) => setPayForm((prev) => ({ ...prev, amount: value ?? 0 }))}
                      currency={invoiceToPay.currency}
                      placeholder="0,00"
                    />
                    {payForm.amount > 0 && payForm.amount < invoiceToPay.pendingAmount && (
                      <p className="text-xs text-warning">
                        Pago parcial — quedarán {formatCurrency(invoiceToPay.pendingAmount - payForm.amount, invoiceToPay.currency)} pendientes
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">Descripción (opcional)</Label>
                    <Input
                      value={payForm.description}
                      onChange={(e) => setPayForm((prev) => ({ ...prev, description: e.target.value }))}
                      placeholder="Se autogenera si se deja vacío"
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse gap-3 pt-4 border-t border-border sm:flex-row sm:justify-end">
                  <Button
                    variant="outline"
                    onClick={() => { setIsPayOpen(false); setInvoiceToPay(null); }}
                    className="w-full rounded-lg sm:w-auto"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handlePay}
                    disabled={isPaying || cashBoxes.length === 0}
                    className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto"
                  >
                    {isPaying ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Procesando...</>
                    ) : (
                      <><DollarSign className="w-4 h-4 mr-2" />Confirmar Pago</>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
